/**
 * Sentry enrichment helpers for API failures (DEG-69).
 *
 * `api.js` attaches `endpoint`, `method`, `status`, `durationMs`, `location` and
 * `isNetworkError` to the errors it throws; these pure functions turn those fields
 * into a normalized fingerprint key and a Sentry capture context so that each
 * failing endpoint + status becomes its own issue instead of collapsing into a
 * single `Heroku Fetch Error` bucket. Network errors (fetch rejected) share one
 * issue, tagged by endpoint.
 *
 * Dependency-free on purpose — reused by `api.js` and both error sagas.
 */

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

/**
 * Strip the query string / hash and replace UUID and numeric path segments with
 * `:id`, so `/clients/123/holdings` and `/clients/456/holdings` (and their UUID
 * equivalents) fingerprint together.
 */
export const normalizeEndpoint = (relativeUrl = '') => {
  if (!relativeUrl) return relativeUrl;

  const path = String(relativeUrl).split('#')[0].split('?')[0];

  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment)) return ':id';
      return segment;
    })
    .join('/');
};

/**
 * Build the `Sentry.captureException` context (`{ tags, contexts, fingerprint }`)
 * from the fields `api.js` set on the error. Defensive: derives the endpoint from
 * `location` when needed and only sets a custom fingerprint when we actually have
 * an endpoint to group by, so genuinely non-API errors keep Sentry's default
 * grouping.
 */
export const apiErrorSentryContext = (error = {}) => {
  const endpoint =
    error.endpoint || (error.location ? normalizeEndpoint(error.location) : undefined);
  const { method, status, durationMs, location } = error;
  const isNetworkError = Boolean(error.isNetworkError);

  const tags = {};
  if (endpoint) tags['api.endpoint'] = endpoint;
  if (method) tags['api.method'] = method;
  if (status != null) tags['api.status'] = String(status);
  if (isNetworkError) tags['api.network_error'] = 'true';

  const captureContext = { tags };

  // Only attach the `api` context and a custom fingerprint when we have an
  // endpoint to group by. Genuinely non-API errors also reach the error saga
  // (e.g. a `normalize()` failure), and they should keep Sentry's default
  // grouping instead of carrying an all-`undefined` `api` context.
  if (endpoint) {
    captureContext.contexts = {
      api: { endpoint, method, status, durationMs, location },
    };
    // A rejected fetch is connectivity (offline, aborted by navigation), not a
    // broken endpoint, so all of them share one issue; its `api.endpoint` tag
    // breakdown shows which endpoints fail most.
    captureContext.fingerprint = isNetworkError
      ? ['api-network-error']
      : [
          'api-error',
          method || 'GET',
          endpoint,
          status != null ? String(status) : 'network',
        ];
  }

  return captureContext;
};
